/**
 * Сканер QR-наклеек внутри PWA.
 *
 * ⚠️ Зачем он вообще, если QR и так ведёт на ссылку. Нативная камера открывает
 * ссылку в Safari, а Fixty стоит как PWA (`display: standalone`) — это ОТДЕЛЬНАЯ
 * сессия, `pm_token` лежит в localStorage другого контекста. Залогиненный в
 * приложении человек по скану нативной камерой попадал бы на экран входа.
 * Поэтому для сотрудника основной вход — камера внутри приложения: та же
 * сессия, та же область данных, наружу не выходим.
 *
 * Ссылка в QR при этом остаётся: у управляющей объектом PWA нет, она сканирует
 * штатной камерой и попадает на публичную страницу.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../api/client';
import { BRAND_NAME } from '../branding';
import Icon from './Icon';
import { track } from '../analytics';
import { extractToken, formatCode } from '../utils/equipmentCode';

// Как часто дёргаем декодер. 8 раз в секунду: чаще — греем телефон впустую,
// реже — человек успевает решить, что «не работает», и убрать камеру.
const DECODE_INTERVAL_MS = 125;

export default function EquipmentScanner({ onResolved, onClose }) {
  const { t } = useTranslation();
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const busyRef = useRef(false);
  const jsqrRef = useRef(null);
  // Последний ОТВЕРГНУТЫЙ чужой QR. Нужен, чтобы не переустанавливать одну и ту
  // же ошибку восемь раз в секунду, пока человек держит камеру над плакатом.
  const foreignRef = useRef(null);

  const [error, setError] = useState(null);
  // idle — камера работает, декодер остановлен: код уже прочитан и лежит в поле.
  const [status, setStatus] = useState('starting'); // starting | scanning | resolving | idle
  const [manual, setManual] = useState('');

  /**
   * Идёт ли распознавание. Отдельно от жизни камеры — и это разделение
   * принципиальное.
   *
   * ⚠️ Раньше декодер работал непрерывно. После неудачного резолва наклейка
   * оставалась в кадре, и каждые 125 мс уходил новый запрос по тому же коду:
   * человек видел мигание «Ищем оборудование… / Код не найден», а сервер за
   * четыре секунды получал тридцать попыток и включал защиту от перебора на
   * живом пользователе.
   *
   * Прочитанный QR — законченная работа: код распознан. Что ответил сервер, к
   * распознаванию отношения не имеет, и повторять его сканированием бессмысленно.
   */
  const [decoding, setDecoding] = useState(false);

  const stopDecoding = useCallback(() => setDecoding(false), []);

  const stop = useCallback(() => {
    setDecoding(false);
    const stream = videoRef.current?.srcObject;
    if (stream) stream.getTracks().forEach((tr) => tr.stop());
    if (videoRef.current) videoRef.current.srcObject = null;
  }, []);

  const resolve = useCallback(async (token, source) => {
    if (busyRef.current) return;
    busyRef.current = true;
    setError(null);
    setStatus('resolving');
    try {
      const res = await api.equipmentResolveTag(token, source);
      track('equipment_scan', { source, state: res.state, in_scope: res.in_scope !== false });
      stop();
      onResolved(res, token);
    } catch (e) {
      // 404 — код не наш или чужой организации. Формулировка нейтральная:
      // «нет такого» и «есть, но не ваш» должны звучать одинаково.
      setError(e?.code === 'RATE_LIMITED' ? t('equipment.scanTooMany') : t('equipment.scanUnknown'));
      // Декодер НЕ перезапускаем: код виден в поле ниже, и дальше решает
      // человек — переправить символ, нажать «Найти» ещё раз или сканировать
      // заново. Автоматический повтор той же ошибки помощи не даёт.
      setStatus('idle');
      busyRef.current = false;
    }
  }, [onResolved, stop, t]);

  /** Один проход декодера по текущему кадру. */
  const tick = useCallback(() => {
    const jsQR = jsqrRef.current;
    if (!jsQR || busyRef.current || !videoRef.current || !canvasRef.current) return;
    const vid = videoRef.current;
    if (!vid.videoWidth) return;

    const c = canvasRef.current;
    // Декодируем центральный квадрат, а не весь кадр: наклейку наводят
    // в рамку, а полный кадр в 1080p — это лишние миллисекунды на каждом
    // тике и нагрев телефона.
    const side = Math.min(vid.videoWidth, vid.videoHeight);
    c.width = 480; c.height = 480;
    const ctx = c.getContext('2d', { willReadFrequently: true });
    ctx.drawImage(vid,
      (vid.videoWidth - side) / 2, (vid.videoHeight - side) / 2, side, side,
      0, 0, 480, 480);

    const img = ctx.getImageData(0, 0, 480, 480);
    const found = jsQR(img.data, img.width, img.height, { inversionAttempts: 'dontInvert' });
    if (!found) return;

    const token = extractToken(found.data);
    if (!token) {
      // Чужой QR — не повод останавливаться: человек мог случайно поймать
      // в кадр наклейку курьера. Но и мигать одним и тем же текстом не надо.
      if (foreignRef.current !== found.data) {
        foreignRef.current = found.data;
        setError(t('equipment.scanForeign', { brand: BRAND_NAME }));
      }
      return;
    }

    // Наш код. Показываем его человеку в поле — он видит, ЧТО именно прочитала
    // камера, и при ошибке распознавания правит символ вместо новой охоты за
    // фокусом на затёртой наклейке.
    stopDecoding();
    foreignRef.current = null;
    setManual(formatCode(token));
    resolve(token, 'in_app');
  }, [resolve, stopDecoding, t]);

  /** Запустить распознавание заново — после ошибки или по кнопке. */
  const startDecoding = useCallback(() => {
    setError(null);
    foreignRef.current = null;
    setStatus('scanning');
    setDecoding(true);
  }, []);

  // Свежий tick в ref, чтобы интервал не пересоздавался на каждом рендере.
  // Без этого пришлось бы положить tick в зависимости эффекта ниже — и таймер
  // перезапускался бы при любом setState, теряя фазу.
  const tickRef = useRef(tick);
  useEffect(() => { tickRef.current = tick; });

  // Жизнь таймера привязана к одному флагу. Так «остановить распознавание»
  // становится обычной сменой состояния, а не ручным clearInterval, который
  // легко забыть в одной из веток.
  useEffect(() => {
    if (!decoding) return undefined;
    const id = setInterval(() => tickRef.current(), DECODE_INTERVAL_MS);
    return () => clearInterval(id);
  }, [decoding]);

  useEffect(() => {
    let alive = true;

    (async () => {
      try {
        // Декодер грузим динамически: он нужен только тем, кто открыл сканер,
        // и тянуть его в основной бандл незачем.
        jsqrRef.current = (await import('jsqr')).default;

        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: 'environment' } },
          audio: false,
        });
        if (!alive) { stream.getTracks().forEach((tr) => tr.stop()); return; }

        const v = videoRef.current;
        if (!v) return;
        v.srcObject = stream;
        // playsInline обязателен: без него iOS открывает видео на весь экран
        // поверх интерфейса и рамку прицела уже не видно.
        v.playsInline = true;
        await v.play();
        if (alive) startDecoding();
      } catch (e) {
        if (!alive) return;
        setError(e?.name === 'NotAllowedError' ? t('equipment.cameraDenied') : t('equipment.cameraFailed'));
        setStatus('idle');
      }
    })();

    return () => { alive = false; stop(); };
  }, [startDecoding, stop, t]);

  const submitManual = (e) => {
    e.preventDefault();
    const token = extractToken(manual);
    if (!token) { setError(t('equipment.scanUnknown')); return; }
    stopDecoding();
    resolve(token, 'manual');
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 3000, background: '#000',
      display: 'flex', flexDirection: 'column',
    }}>
      <video ref={videoRef} muted playsInline
             style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
      <canvas ref={canvasRef} style={{ display: 'none' }} />

      {/* Рамка прицела — она же подсказка, куда наводить. */}
      <div style={{
        position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)',
        width: 'min(70vw, 300px)', aspectRatio: '1', border: '3px solid rgba(255,255,255,0.9)',
        borderRadius: 16, boxShadow: '0 0 0 100vmax rgba(0,0,0,0.45)',
      }} />

      <div style={{ position: 'absolute', top: 'max(16px, env(safe-area-inset-top))', right: 16, zIndex: 2 }}>
        <button onClick={() => { stop(); onClose(); }} aria-label={t('common.close')}
                style={{ background: 'rgba(0,0,0,0.55)', border: 0, borderRadius: 999,
                         width: 40, height: 40, color: '#fff', cursor: 'pointer' }}>
          <Icon name="x" size={20} />
        </button>
      </div>

      <div style={{
        position: 'absolute', bottom: 0, left: 0, right: 0, zIndex: 2,
        padding: `16px 16px calc(16px + env(safe-area-inset-bottom))`,
        background: 'linear-gradient(transparent, rgba(0,0,0,0.75))', color: '#fff',
      }}>
        <div style={{ textAlign: 'center', fontSize: 14, marginBottom: 10 }}>
          {status === 'resolving' ? t('equipment.scanResolving')
            : error || t('equipment.scanHint')}
        </div>

        {/* Ввод руками — не «на всякий случай», а штатный путь: наклейка на
            кухне затирается, и код под QR печатается именно для этого.
            Сюда же ложится распознанный код: человек видит, что именно
            прочитала камера, и правит символ вместо новой охоты за фокусом. */}
        <form onSubmit={submitManual} style={{ display: 'flex', gap: 8 }}>
          <input className="form-input" value={manual} onChange={(e) => setManual(e.target.value)}
                 placeholder={t('equipment.scanManualPlaceholder')}
                 autoCapitalize="characters" autoCorrect="off" spellCheck="false"
                 style={{ flex: 1, background: 'rgba(255,255,255,0.95)' }} />
          <button type="submit" className="btn btn-primary"
                  data-ph-capture="equipment_scan_manual"
                  disabled={!manual.trim() || status === 'resolving'}>
            {t('equipment.scanGo')}
          </button>
        </form>

        {/* Декодер остановлен — вернуть его может только человек. Кнопка
            появляется вместо автоповтора: он лишь мигал бы той же ошибкой. */}
        {status === 'idle' && (
          <button className="btn btn-sm" data-ph-capture="equipment_scan_retry"
                  style={{ width: '100%', marginTop: 8 }}
                  onClick={startDecoding}>
            <Icon name="scan" size={14} /> {t('equipment.scanAgain')}
          </button>
        )}
      </div>
    </div>
  );
}
